import React from 'react';
import { Range } from 'slate';
import useLastFocusedEditorSelection from 'components/text-editor/hooks/use-last-focused-editor-selection';
import { makeVar, useReactiveVar } from '@apollo/client';
import { isFunction } from 'lodash';
import { CustomEditor } from 'components/text-editor/hooks/use-custom-editor';
import { ReactEditor, useFocused } from 'slate-react';
import { getBrowserTextSelection } from 'utils/js';

interface UseSelectedText {
  selectedTextSnippet: any;
  setSelectedTextSnippet(selectedTextSnippet: any): void;
  updateSelectedTextSnippet(data: any): void;
  markNodeAsSelected(at?: Range): void;
  clearSelectedTextSnippet: Function;
}

export const selectedTextSnippetVar = makeVar<any>(undefined);

export default function useTextSnippetSelection(
  editor: CustomEditor,
): UseSelectedText {
  const selectedTextSnippet = useReactiveVar(selectedTextSnippetVar);

  const editorSelection = useLastFocusedEditorSelection(editor);

  function markNodeAsSelected(at = editorSelection) {
    const selectedNode = editor.getSelectedNode();

    if (selectedNode) {
      editor.unMarkNodesAsSelected();
    }

    editor.markNodesAsSelected(editorSelection);
  }

  function clearSelectedTextSnippet() {
    setSelectedTextSnippet(undefined);
  }

  function updateSelectedTextSnippet(data) {
    setSelectedTextSnippet((selectedTextSnippet) => {
      if (selectedTextSnippet) {
        return { ...selectedTextSnippet, ...data };
      }
      return selectedTextSnippet;
    });
  }

  return {
    selectedTextSnippet,
    setSelectedTextSnippet,
    clearSelectedTextSnippet,
    updateSelectedTextSnippet,
    markNodeAsSelected,
  };
}

function setSelectedTextSnippet(selectedTextSnippet) {
  if (isFunction(selectedTextSnippet)) {
    return selectedTextSnippetVar(
      selectedTextSnippet(selectedTextSnippetVar()),
    );
  }

  selectedTextSnippetVar(selectedTextSnippet);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
function useLastFocusedWindowSelection(
  editor: ReactEditor,
): string | undefined {
  const isEditorFocused = useFocused();
  const editorSelection = React.useRef<any>(editor.selection);

  React.useEffect(() => {
    if (isEditorFocused) {
      editorSelection.current = getBrowserTextSelection();
    }
  }, [editor.selection, isEditorFocused]);

  return editorSelection.current;
}
